import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Input, InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { RenderModalFunction, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Select } from "@amzn/stencil-react-components/select";
import { H1, Text } from "@amzn/stencil-react-components/text";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import { useFieldValidation } from "common/hooks/useFieldValidation";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useState } from "react";
import { useHistory } from "react-router-dom";

// TODO: Replace with real data from lists API
const MOCK_USER_LISTS = [
  { label: "UX Design and Prototyping tools", value: "lst-ux-design", isPublic: true },
  { label: "UX Research tools", value: "lst-ux-research", isPublic: true },
  { label: "My Draft List (private)", value: "lst-draft", isPublic: false },
  { label: "Security Tools", value: "lst-security", isPublic: true },
  { label: "Productivity Suite", value: "lst-productivity", isPublic: true },
];

interface CreateSpaceModalProps {
  trigger: (open: () => void) => React.ReactNode;
}

const ALIAS_PATTERN = /^[a-z0-9_-]*$/;

export const CreateSpaceModal: FC<CreateSpaceModalProps> = ({ trigger }) => {
  const history = useHistory();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [alias, setAlias] = useState("");
  const [selectedLists, setSelectedLists] = useState<string[]>([]);
  const [aliasAvailable, setAliasAvailable] = useState<boolean | null>(null);

  const aliasValidation = useFieldValidation(alias, ALIAS_PATTERN, getString("spaces.create.aliasValidation"));
  const listOptions = MOCK_USER_LISTS.filter((list) => list.isPublic).map((list) => list.label);
  const isFormValid =
    title.trim() &&
    description.trim() &&
    selectedLists.length > 0 &&
    !aliasValidation.hasError &&
    aliasAvailable !== false;

  const handleAliasChange = (value: string) => {
    setAlias(value.toLowerCase());
    const clean = value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    // TODO: Replace with real API check (debounced GetItem)
    setAliasAvailable(clean.length > 0 ? clean !== "taken-alias" : null);
  };

  const handleCreate = (close: () => void) => {
    // TODO: Replace with real API call
    close();
    history.push(`/aiforme/spaces/${alias || "new-space"}`);
  };

  const renderTitleField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.create.pageTitle")} *</Text>
      <Input
        value={title}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
        placeholder={getString("spaces.create.titlePlaceholder")}
        data-testid="create-space-title"
      />
    </Col>
  );

  const renderDescriptionField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.create.description")} *</Text>
      <TextArea
        value={description}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
        placeholder={getString("spaces.create.descriptionPlaceholder")}
        touchpoint="create-space-description"
        data-testid="create-space-description"
      />
      <Text color="color.text.secondary" fontSize="T100">
        {getString("spaces.create.descriptionHint")}
      </Text>
    </Col>
  );

  const getAliasFooter = () => {
    if (aliasValidation.hasError) return aliasValidation.errorMessage;
    if (aliasAvailable === true) return getString("spaces.create.aliasAvailable", { alias });
    if (aliasAvailable === false) return getString("spaces.create.aliasTaken");
    return getString("spaces.create.aliasHint");
  };

  const renderAliasField = () => (
    <Col gridGap="dimensions.spacing.100">
      <InputWrapper
        id="create-space-alias"
        labelText={getString("spaces.create.alias")}
        error={aliasValidation.hasError}
        footer={getAliasFooter()}
      >
        {(inputProps) => (
          <Input
            {...inputProps}
            value={alias}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAliasChange(e.target.value)}
            placeholder="my-team-space"
          />
        )}
      </InputWrapper>
    </Col>
  );

  const renderListsField = () => (
    <Col gridGap="dimensions.spacing.100">
      <Text fontWeight="bold">{getString("spaces.create.addLists")} *</Text>
      <Select
        options={listOptions}
        isMulti
        placeholder={getString("spaces.create.addListsPlaceholder")}
        onChange={(values: any) => setSelectedLists(Array.isArray(values) ? values : [values])}
        data-testid="create-space-lists"
      />
    </Col>
  );

  const renderActions = (close: () => void) => (
    <Row justifyContent="flex-end" gridGap="dimensions.spacing.200" margin={{ top: "dimensions.spacing.200" }}>
      <Button variant={ButtonVariant.Secondary} onClick={close}>
        {getString("spaces.create.cancel")}
      </Button>
      <Button
        variant={ButtonVariant.Primary}
        onClick={() => handleCreate(close)}
        disabled={!isFormValid}
        data-testid="create-space-submit"
      >
        {getString("spaces.create.submit")}
      </Button>
    </Row>
  );

  const renderModal: RenderModalFunction = ({ close }: RenderModalFunctionParams) => (
    <Col padding="dimensions.spacing.500" gridGap="dimensions.spacing.400" style={{ maxWidth: 640 }}>
      <H1>{getString("spaces.create.title")}</H1>
      {renderTitleField()}
      {renderDescriptionField()}
      {renderAliasField()}
      {renderListsField()}
      <Text color="color.text.secondary" fontSize="T100">
        {getString("spaces.create.visibilityNote")}
      </Text>
      {renderActions(close)}
    </Col>
  );

  return (
    <WithModal renderModal={renderModal}>
      {({ open }) => (
        <>
          {trigger(() => {
            setTitle("");
            setDescription("");
            setAlias("");
            setSelectedLists([]);
            setAliasAvailable(null);
            open();
          })}
        </>
      )}
    </WithModal>
  );
};
